import type { ApolloClient } from '@apollo/client';
import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from '@savvy-kit/components/button';
import { cn } from '@savvy-kit/lib/utils';
import Banner from '@savvy-kit/patterns/banner';
import { graphql } from 'src/gql';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';
import { removeSalesforceAccessToken } from '@savvy/deprecated-shared/utils/salesforce';
import { removeLoggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

export const STOP_IMPERSONATING_USER = graphql(/* GraphQL */ `
  mutation StopImpersonatingUser($input: StopImpersonatingUserInput!) {
    stopImpersonatingUser(input: $input) {
      trueUser {
        id
      }
      currentUser {
        id
      }
    }
  }
`);

export function LogoutAsButton({
  useGenericCopy = false,
  destinationUrl,
  className,
  compactOnSmallScreens = false,
}: {
  readonly useGenericCopy?: boolean;
  readonly destinationUrl?: string;
  readonly className?: string;
  readonly compactOnSmallScreens?: boolean;
}) {
  const { user: authUser, logout } = useAuth();
  const [stopImpersonatingUser] = useMutationWithErrorLogging(STOP_IMPERSONATING_USER);
  const apolloClient: ApolloClient<object> = useApolloClient();
  const [error, setError] = useState<string | null>(null);

  const handleError = async (error: unknown, errorType: string) => {
    datadogRum.addError(error, {
      user_id: authUser?.id,
      action: 'logout',
      error_type: errorType,
    });

    await logout();
    LocalStorage.removeItem('accessToken');
    removeSalesforceAccessToken();
    removeLoggedInAsAdvisorID();
    setError('Something went wrong; please log in again. Redirecting to login page...');
    setTimeout(() => {
      window.location.href = '/login';
    }, 3500);
  };

  const handleLogoutFromClient = async () => {
    try {
      await stopImpersonatingUser({
        variables: {
          input: {},
        },
      });

      removeLoggedInAsAdvisorID();
      try {
        await apolloClient.clearStore();
      } catch (clearStoreError) {
        handleError(clearStoreError, 'Error clearing Apollo store');
        return;
      }
      window.location.href = destinationUrl ?? '/';
    } catch (error) {
      handleError(error, 'Error stopping impersonation');
    }
  };

  const fullLabel = useGenericCopy
    ? 'Log out of client account'
    : `Log out of ${authUser?.firstName} ${authUser?.lastName}`;

  return (
    <>
      {error !== null && <Banner variant="destructive" description={error} />}
      <Button
        size="sm"
        variant="secondary"
        className={cn('mr-8 self-center', className)}
        onClick={handleLogoutFromClient}
        aria-label={fullLabel}
      >
        {compactOnSmallScreens ? (
          <>
            <span className="sm:hidden">Log out</span>
            <span className="hidden sm:inline">{fullLabel}</span>
          </>
        ) : (
          fullLabel
        )}
      </Button>
    </>
  );
}
